import { facetSetHas } from "./pricingCatalogFacets.js";

const RECIPE_ID = /^[0-9a-f-]{36}$/i;

/** Width in feet from a size label such as `14x32`. */
export function widthFromSizeBand(size: string): number | null {
  const match = size.trim().match(/^(\d+(?:\.\d+)?)\s*x/i);
  if (!match) return null;
  const width = Number(match[1]);
  return Number.isFinite(width) && width > 0 ? width : null;
}

export function columnAppliesRecipeIds(metadata: unknown): string[] | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const raw = (metadata as Record<string, unknown>).applies_recipe_ids;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const ids = raw.filter((id): id is string => typeof id === "string" && RECIPE_ID.test(id));
  return ids.length > 0 ? ids : null;
}

/** `null` clears the list. An empty or malformed list is invalid. */
export function parseAppliesRecipeIds(raw: unknown): string[] | null | "invalid" {
  if (raw === null) return null;
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > 2000) return "invalid";
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== "string" || !RECIPE_ID.test(item)) return "invalid";
    const id = item.toLowerCase();
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function sameRecipeIdList(a: readonly string[] | null, b: readonly string[] | null): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null || a.length !== b.length) return false;
  const left = a.map((id) => id.toLowerCase()).sort();
  const right = b.map((id) => id.toLowerCase()).sort();
  return left.every((id, index) => id === right[index]);
}

export type ProductScopeRow = {
  subject_kind: string;
  subject_id: string;
  name: string;
  series: string;
  roof_style: string;
  size_band: string;
  recipe_kind?: string | null;
};

export function productScopePool(rows: readonly ProductScopeRow[], productsOnly: boolean): ProductScopeRow[] {
  return rows.filter((row) => {
    if (row.subject_kind !== "recipe") return false;
    if (!productsOnly) return true;
    return !row.recipe_kind || row.recipe_kind === "product";
  });
}

export function filterProductScopeRows(
  rows: readonly ProductScopeRow[],
  filters: {
    groups: readonly string[];
    styles: readonly string[];
    sizes: readonly string[];
    widths: readonly string[];
  },
): ProductScopeRow[] {
  const widths = new Set(filters.widths);
  return rows.filter((row) => {
    if (!facetSetHas(new Set(filters.groups), row.series)) return false;
    if (!facetSetHas(new Set(filters.styles), row.roof_style)) return false;
    if (!facetSetHas(new Set(filters.sizes), row.size_band)) return false;
    if (widths.size === 0) return true;
    const width = widthFromSizeBand(row.size_band);
    return width != null && widths.has(String(width));
  });
}

export function widthsInRows(rows: readonly ProductScopeRow[]): string[] {
  const widths = new Set<string>();
  for (const row of rows) {
    const width = widthFromSizeBand(row.size_band);
    if (width != null) widths.add(String(width));
  }
  return [...widths].sort((a, b) => Number(a) - Number(b));
}
